// The Hiring Request (`#requests/new`, `#requests/RQ-####`; lane B2 S08; replaces V1's RequestForm and RequestPage):
// one record, one body across his three steps — "HRBP request creation, HM certification, HRBP approval" (G §4 step 1).
// Under the header the Journey; the body is the request's fields with the Current JD as a full-width band in the centre
// (B13 F-144: no side column); under them the decision card
// of the seat that acts now, the three parallel tiles once approved ("HRBP approval triggers TA and Compensation in
// parallel."), and the request's History. Every control is drawn from the record's `can` (the server decides).
// Hosted by MissionShell: views/b2/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useMemo, useState } from "react";
import { AppError } from "@/api/runtime";
import {
  compReviewOpen, requestCreate, requestDraftOptions, requestOpen, requestSubmit, requestUpdate, requisitionCreate, requisitionOpen,
  type RequestOpenOutput,
} from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { go } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import {
  ACCESS_LABELS, ACTION_LABELS, Banner, Button, Card, CORE_LABELS, COMPENSATION_REVIEW_STATUS, ErrorStrip, Field, Flex, G6_CAPABILITY_LABELS,
  History, HISTORY_LABELS, JOURNEY_LABELS, Journey, KeyValue, LEVEL_DISPLAY, PageHeader, Pill, Recorded, RECRUITER_ASSIGNMENT_STATUS,
  REQUEST_STATUS, REQUISITION_STATUS, RULE_LABELS, Skeleton, Stack, TAG_LABELS, TextArea, TextLink, formatDate, type HistoryEvent,
  type JourneyStep, type PersonOption,
} from "@/mc";
import { BRAND_CHOICES, changedColumns, draftOf, EMPTY_DRAFT, fieldsOf, type RequestDraft } from "./compat";
import { FIELD_WORD, fieldErrorWords, refusalSentence, STATUS_WORD, useSettledRead } from "./data";
import { Decisions } from "./Decisions";
import { convertedTo, REQUEST_PAGE_LABELS as P, REQUEST_RULES as R, TIMING_LABELS as T } from "./labels";
import { HEADCOUNT_WORD, missingOf, NEW_REPLACEMENT_WORD, POSTING_SCOPE_WORD, RequestFields, URGENCY_WORD } from "./RequestFields";
import { creationLabel, DRAFT_STATES, journeySteps } from "./journey";


export function HiringRequest({ id }: { id?: string | undefined }) {
  return id ? <ExistingRequest key={id} id={id} /> : <NewRequest />;
}

// ---- #requests/new: the HR Business Partner creates the request ------------------------------------------------------

function NewRequest() {
  const { me } = useSession();
  const [draft, setDraft] = useState<RequestDraft>({ ...EMPTY_DRAFT });
  const options = useDraftOptions(true, draft.department);
  const legacyAdmin = !!me?.roles.includes("admin") && !me.roles.includes("hrbp");
  const create = useWrite((input: Parameters<typeof requestCreate>[0], key: string) => requestCreate(input, key), [["request"]]);
  const submit = useWrite((input: { request_id: string }, key: string) => requestSubmit(input, key), [["request"]]);
  const [error, setError] = useState<unknown>(null);
  const taxonomy = { job_family: false, area_of_interest: false };
  const missing = missingOf(draft, taxonomy);
  const busy = create.isPending || submit.isPending;

  const save = async (andSubmit: boolean) => {
    setError(null);
    try {
      const made = await create.mutateAsync({
        ...fieldsOf(draft),
        hiring_manager_id: draft.hiring_manager_id || null,
        requester_id: legacyAdmin ? draft.requester_id || null : null,
        job_description: draft.job_description.trim() || null,
      });
      if (andSubmit) await submit.mutateAsync({ request_id: made.id });
      go(`#requests/${made.id}`);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Stack gap={16}>
      <PageHeader title={P.hiringRequest} pills={<Pill>{REQUEST_STATUS.draft}</Pill>} />
      <Journey label={P.hiringRequest} steps={journeySteps(null)} />
      <Refusal error={error} />
      <RequestFields
        draft={draft}
        onChange={setDraft}
        mode="edit"
        hmOptions={options.hms}
        hrbpOptions={legacyAdmin ? options.hrbps : undefined}
        taxonomyRequired={taxonomy}
        footer={<FormActions missing={missing} busy={busy} onSave={() => void save(false)} onSubmit={() => void save(true)} />}
      />
    </Stack>
  );
}

function useDraftOptions(enabled: boolean, department: string): { hms: PersonOption[]; hrbps: PersonOption[] } {
  const { can } = useSession();
  const q = useSettledRead(
    ["request", "draft-options", department],
    () => requestDraftOptions({ department: department.trim() || null }),
    { enabled: enabled && can("request.draft_options") },
  );
  return useMemo(() => ({
    hms: (q.data?.requesters ?? []).map((p) => ({ value: p.person_id, name: p.name })),
    hrbps: (q.data?.reviewers ?? []).map((p) => ({ value: p.person_id, name: p.name })),
  }), [q.data]);
}

// ---- #requests/RQ-####: one request, every seat ----------------------------------------------------------------------

/** The request's loading geometry in the design system's skeletons: the header, the Journey and the fields card at their
 * final heights (DC-25). The record route's guard (views/b2/register.ts) draws it too while the request's read is out. */
export function RequestLoading() {
  return (
    <Stack gap={16}>
      <PageHeader title={P.hiringRequest} loading />
      <Skeleton height={88} />
      <Card loading={{ lines: 8 }} />
    </Stack>
  );
}

function ExistingRequest({ id }: { id: string }) {
  const { register } = useViewState();
  const q = useSettledRead(["request", id], () => requestOpen({ request_id: id }));
  useEffect(() => register({ view: "request", ids: { request: id }, summary: { loading: q.isLoading, ...(q.data ? { status: q.data.status } : {}) } }), [register, id, q.isLoading, q.data?.status]);
  if (q.isLoading) return <RequestLoading />;
  if (q.error || !q.data) {
    const refused = q.error ? refusalSentence(q.error) : null;
    return (
      <Stack gap={16}>
        <PageHeader title={P.hiringRequest} />
        {refused ? <Banner tone="red" live>{refused}</Banner> : (
          <ErrorStrip
            message={RULE_LABELS.failedSyncRetryable}
            problem={q.error ? { status: q.error.status, refusal_code: q.error.code, request_id: q.error.requestId } : undefined}
            onRetry={() => void q.refetch()}
            
          />
        )}
      </Stack>
    );
  }
  return <RequestBody r={q.data} />;
}

function RequestBody({ r }: { r: RequestOpenOutput }) {
  const can = r.can ?? {};
  const editing = !!can.update && DRAFT_STATES.has(r.status);
  const certifying = !!can.certify && r.status === "pending_hm_cert";
  return (
    <Stack gap={16}>
      <PageHeader
        title={P.hiringRequest}
        pills={<Pill>{STATUS_WORD[r.status] ?? r.status}</Pill>}
        meta={[
          <span className="mc-mono" key="id">{r.id}</span>,
          r.title || null,
          ...(r.converted_requisition_id ? [<ConvertedLink key="converted" requisitionId={r.converted_requisition_id}  />] : []),
        ].filter(Boolean)}
      />
      <Journey label={P.hiringRequest} steps={journeySteps(r)} />
      {editing ? <EditRequest r={r} /> : certifying ? <Decisions r={r} mode="certify" /> : <ViewRequest r={r} />}
      {!editing && !certifying ? <Decisions r={r} mode="decide" /> : null}
      {r.status === "approved" || r.status === "converted" ? <ApprovedInParallel r={r} /> : null}
      <RequestHistory r={r} />
    </Stack>
  );
}

/** The record's values, read-only (every seat that is not acting on the form now). A seat with nothing to act on may
 * hold no control on the page at all, so the values are one labelled stop a keyboard can reach and scroll from (the
 * read-only well's pattern, as DocPane's). */
export function ViewRequest({ r }: { r: RequestOpenOutput }) {
  const draft = draftOf(r);
  return (
    <div role="region" aria-label={P.hiringRequest} tabIndex={0}>
      <RequestFields
        draft={draft}
        onChange={() => undefined}
        mode="view"
        hmName={r.intended_hm_name ?? r.requester_name}
        compMode={"target_comp_min" in r || "target_comp_max" in r ? "view" : "hidden"}
      />
    </div>
  );
}

// ---- the originator's draft: Save and Submit -------------------------------------------------------------------------

function EditRequest({ r }: { r: RequestOpenOutput }) {
  const base = useMemo(() => draftOf(r), [r]);
  const [draft, setDraft] = useState<RequestDraft>(base);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [recorded, setRecorded] = useState(false);
  const options = useDraftOptions(true, draft.department);
  const invalidate = [["request"]] as const;
  const update = useWrite((input: Parameters<typeof requestUpdate>[0], key: string) => requestUpdate(input, key), invalidate);
  const submit = useWrite((input: { request_id: string }, key: string) => requestSubmit(input, key), invalidate);
  const routed = r.events.some((event) => event.kind === "routed_to_hm" || event.kind === "submitted");
  const changes = changedColumns(base, draft);
  const hmChanged = draft.hiring_manager_id !== base.hiring_manager_id;
  const jdChanged = draft.job_description !== base.job_description;
  const dirty = Object.keys(changes).length > 0 || hmChanged || jdChanged;
  const needsReason = dirty && (routed || (jdChanged && !!base.job_description));
  const taxonomy = {
    job_family: r.missing_for_submit.includes("job_family"),
    area_of_interest: r.missing_for_submit.includes("area_of_interest"),
  };
  const missing = missingOf(draft, taxonomy);
  const reasonOk = !needsReason || reason.trim().length >= 3;

  const save = async (): Promise<boolean> => {
    if (!dirty) return true;
    await update.mutateAsync({
      request_id: r.id,
      ...changes,
      ...(hmChanged ? { hiring_manager_id: draft.hiring_manager_id } : {}),
      ...(jdChanged ? { job_description: draft.job_description } : {}),
      ...(jdChanged && base.job_description ? { job_description_reason: reason.trim() } : {}),
      ...(routed ? { change_reason: reason.trim() } : {}),
      expected_version: r.row_version ?? null,
    });
    return true;
  };
  const run = async (andSubmit: boolean) => {
    setError(null);
    setRecorded(false);
    try {
      await save();
      if (andSubmit) await submit.mutateAsync({ request_id: r.id });
      setReason("");
      setRecorded(true);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <>
      <Refusal error={error} />
      <RequestFields
        draft={draft}
        onChange={setDraft}
        mode="edit"
        hmOptions={options.hms}
        hmName={r.intended_hm_name ?? undefined}
        taxonomyRequired={taxonomy}
        footer={(
          <>
            {needsReason ? (
              <Field label={HISTORY_LABELS.reason} required hint={DIFF_HINT}>
                <TextArea value={reason} onChange={(event) => setReason(event.target.value)} rows={2}  />
              </Field>
            ) : null}
            <FormActions
              missing={missing}
              busy={update.isPending || submit.isPending}
              saveBlocked={reasonOk ? undefined : R.fieldOwnership}
              onSave={() => void run(false)}
              onSubmit={() => void run(true)}
            />
            {recorded ? <Recorded live>{CORE_LABELS.recordedAuditEvent}</Recorded> : null}
          </>
        )}
      />
    </>
  );
}

const DIFF_HINT = R.fieldOwnership;

/** Save (ghost) · Submit (the one red primary), Submit dashed until every required field is set, its description his rule
 * sentence (DC-24). No list of the missing fields under it (F-142): every required field carries its Required marker. */
export function FormActions({ missing, busy, saveBlocked, onSave, onSubmit }: { missing: readonly string[]; busy: boolean; saveBlocked?: string | undefined; onSave: () => void; onSubmit: () => void }) {
  return (
    <Flex gap={8} wrap>
      <Button tone="ghost" onClick={onSave} busy={busy} blocked={saveBlocked ? { reason: saveBlocked } : undefined} >{ACTION_LABELS.save}</Button>
      <Button
        tone="primary"
        onClick={onSubmit}
        busy={busy}
        blocked={missing.length > 0 ? { reason: R.requiredFields } : saveBlocked ? { reason: saveBlocked } : undefined}
        
      >
        {ACTION_LABELS.submit}
      </Button>
    </Flex>
  );
}

/** A converted request's line (F-148): *Converted → R-####*, a link to the requisition it became. */
export function ConvertedLink({ requisitionId, hs }: { requisitionId: string; hs?: string | undefined }) {
  return <TextLink href={`#requisitions/${requisitionId}`} hs={hs}>{convertedTo({ requisition: requisitionId })}</TextLink>;
}

/** A refused write: his rule sentence at the top of the form, values kept; the fields it names under it. */
export function Refusal({ error }: { error: unknown }) {
  if (!error) return null;
  const sentence = refusalSentence(error);
  if (!sentence) {
    const problem = error instanceof AppError ? { status: error.status, refusal_code: error.code, request_id: error.requestId } : { status: 0, refusal_code: "NETWORK" };
    return <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={problem} live />;
  }
  const fields = fieldErrorWords(error);
  return <Banner tone="red" live right={fields.length ? fields.join(" · ") : undefined}>{sentence}</Banner>;
}

// ---- approved: the three parallel starts, and the Coordinator's create card ------------------------------------------

function ApprovedInParallel({ r }: { r: RequestOpenOutput }) {
  const { can } = useSession();
  const requisitionId = r.converted_requisition_id;
  const req = useSettledRead(["requisition", requisitionId], () => requisitionOpen({ requisition_id: requisitionId! }), { enabled: !!requisitionId && can("requisition.open") });
  const comp = useSettledRead(["comp-review", r.id], () => compReviewOpen({ request_id: r.id }), { enabled: can("comp_review.open") });
  const full = req.data && "assignment_status" in req.data ? req.data : null;
  const creation = !requisitionId ? REQUISITION_STATUS.reqCreationPending : full?.posted_at ? REQUISITION_STATUS.posted : REQUISITION_STATUS.created;
  const assigned = full ? full.assignment_status === "assigned" : !!r.routing_recruiter_id;
  const compWord = { pending: COMPENSATION_REVIEW_STATUS.pending, in_review: COMPENSATION_REVIEW_STATUS.inReview, finalized: COMPENSATION_REVIEW_STATUS.finalized }[comp.data?.status ?? ""];
  const create = useWrite((input: { request_id: string }, key: string) => requisitionCreate(input, key), [["request"], ["requisition"]]);
  const [error, setError] = useState<unknown>(null);
  const tiles: JourneyStep[] = [
    {
      label: JOURNEY_LABELS.coordinatorCreatesPosts, icon: "send", state: full?.posted_at ? "done" : "active",
      pill: <Pill>{creation}</Pill>, href: requisitionId ? `#requisitions/${requisitionId}` : undefined, hs: "req.tile-coordinator",
    },
    {
      label: JOURNEY_LABELS.taManagerAssignsRecruiter, icon: "users", state: assigned ? "done" : "active",
      pill: <Pill>{assigned ? RECRUITER_ASSIGNMENT_STATUS.assigned : RECRUITER_ASSIGNMENT_STATUS.pendingTaManager}</Pill>,
      href: can("recruiter.queue") ? "#requisitions/assignment" : undefined, hs: "req.tile-ta-manager",
    },
    {
      label: JOURNEY_LABELS.compensationStartsInParallel, icon: "dollar", state: comp.data?.status === "finalized" ? "done" : "active",
      pill: compWord ? <Pill>{compWord}</Pill> : undefined, href: can("comp_review.queue") ? `#compensation/${r.id}` : undefined, hs: "req.tile-compensation",
    },
  ];
  const mayCreate = !!r.can?.create_requisition;
  return (
    <Stack gap={12}>
      <Journey label={JOURNEY_LABELS.inParallel} parallel steps={tiles} />
      {mayCreate ? (
        <Card title={G6_CAPABILITY_LABELS.createPostReq} pills={<Pill>{REQUISITION_STATUS.reqCreationPending}</Pill>}>
          <Refusal error={error} />
          <KeyValue
            cols={2}
            items={[
              [T.targetStartDate, r.target_start ? formatDate(r.target_start, "UTC") : "—"],
              [T.urgency, URGENCY_WORD[r.urgency] ?? "—"],
            ]}
          />
          <Flex gap={8}>
            <Button
              tone="primary"
              busy={create.isPending}
              onClick={() => {
                setError(null);
                create.mutateAsync({ request_id: r.id }).then((made) => go(`#requisitions/${made.requisition_id}`), setError);
              }}
              
            >
              {ACTION_LABELS.create}
            </Button>
          </Flex>
        </Card>
      ) : null}
    </Stack>
  );
}

// ---- History: every event and every original/revised value, append-only ---------------------------------------------

/**
 * His word for each act on the request. V1's question and answer read as his chain: "Returned request" replaces
 * `returned` and `question_raised` (G §5), and an answer sends the request back to its HR Business Partner. An act with no
 * word of his (V1's withdrawal) has none and is left out of the History, never drawn as its key.
 */
const EVENT_WORD: Readonly<Record<string, string>> = {
  created: JOURNEY_LABELS.hrbpRequestCreation,
  routed_to_hm: REQUEST_STATUS.pendingHmCertification,
  hm_certified: REQUEST_STATUS.hmCertified,
  submitted: REQUEST_STATUS.pendingHrbpApproval,
  resubmitted: REQUEST_STATUS.pendingHrbpApproval,
  question_raised: REQUEST_STATUS.returned,
  answered: REQUEST_STATUS.pendingHrbpApproval,
  approved: REQUEST_STATUS.approved,
  returned: REQUEST_STATUS.returned,
  dormant: REQUEST_STATUS.dormant,
  marked_dormant: REQUEST_STATUS.dormant,
  converted: REQUISITION_STATUS.created,
  ta_started: `${JOURNEY_LABELS.coordinatorCreatesPosts} · ${JOURNEY_LABELS.taManagerAssignsRecruiter}`,
  comp_requested: JOURNEY_LABELS.compensationStartsInParallel,
};

export function requestEventWord(kind: string): string | undefined {
  return EVENT_WORD[kind];
}

/** The choice fields whose stored values have his words (a value outside the list reads as no value, never its key). */
const VALUE_WORD: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  posting_scope: POSTING_SCOPE_WORD,
  reason: NEW_REPLACEMENT_WORD,
  budget_status: HEADCOUNT_WORD,
  urgency: URGENCY_WORD,
  level: LEVEL_DISPLAY,
  brands: Object.fromEntries(BRAND_CHOICES.map((choice) => [choice.value, choice.label])),
};
const PERSON_FIELDS = new Set(["intended_hm_id", "hiring_manager_id"]);

/** A field version's value as the screen shows it: the choice's word, the person's name, Yes or No, or the typed text. */
export function shownValue(value: unknown, field = "", names: ReadonlyMap<string, string> = new Map()): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.map((item) => shownValue(item, field, names)).join(", ") : "—";
  if (typeof value === "boolean") return value ? ACCESS_LABELS.Yes : ACCESS_LABELS.No;
  const words = VALUE_WORD[field];
  if (words) return words[String(value)] ?? "—";
  if (PERSON_FIELDS.has(field)) return names.get(String(value)) ?? "—";
  return String(value);
}

type FieldVersion = { field: string; old_value: unknown; new_value: unknown; by_id?: string; seat?: string; reason?: string | null; at: string };

/** The request's History, newest first: each act in his words with its note, and each revised field with its old and
 * new value; an act or a field with no word of his is left out. */
export function requestHistory(
  r: Pick<RequestOpenOutput, "events" | "field_versions" | "intended_hm_id" | "intended_hm_name"> & Partial<Pick<RequestOpenOutput, "requester_id">>,
): HistoryEvent[] {
  const versions = (r.field_versions ?? []) as FieldVersion[];
  const names = new Map(r.events.filter((event) => event.by_id && event.by_name).map((event) => [event.by_id!, event.by_name!]));
  if (r.intended_hm_id && r.intended_hm_name) names.set(r.intended_hm_id, r.intended_hm_name);
  const events: HistoryEvent[] = [];
  for (const event of r.events) {
    // the creation is named for its actor, as the Journey's first step is (F-51)
    const word = event.kind === "created" ? creationLabel({ requester_id: r.requester_id ?? "", intended_hm_id: r.intended_hm_id ?? null }) : EVENT_WORD[event.kind];
    if (!word) continue;
    const note = event.comment || event.question || event.answer;
    events.push({
      id: event.id,
      actor: event.by_name ? { name: event.by_name, hueKey: event.by_id ?? undefined } : undefined,
      sentence: note ? <Stack gap={2}><span>{word}</span><span className="mc-hint">{note}</span></Stack> : word,
      at: event.at,
      tone: event.kind === "returned" || event.kind === "question_raised" ? "warn" : event.kind === "approved" ? "ok" : undefined,
    });
  }
  versions.forEach((version, index) => {
    const word = FIELD_WORD[version.field];
    if (!word || version.old_value === null || version.old_value === undefined) return;
    const name = version.by_id ? names.get(version.by_id) : undefined;
    events.push({
      id: `fv-${index}`,
      actor: name ? { name, hueKey: version.by_id } : undefined,
      sentence: word,
      at: version.at,
      source: version.seat === "hm" ? TAG_LABELS.revised : undefined,
      audit: {
        old: shownValue(version.old_value, version.field, names),
        new: shownValue(version.new_value, version.field, names),
        reason: version.reason ?? undefined,
      },
    });
  });
  return events.sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

function RequestHistory({ r }: { r: RequestOpenOutput }) {
  return (
    <Card title={HISTORY_LABELS.history} id="req-history">
      <History label={HISTORY_LABELS.history} events={requestHistory(r)} empty="—" />
    </Card>
  );
}

